import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Check, Zap, Shield, Mail, Layers } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { apiErrorMessage } from "../lib/errors";

const PRO_FEATURES = [
  { icon: Zap, text: "Unlimited AI-handicapped research across MLB / NBA / NHL / NFL / UFC" },
  { icon: Shield, text: "Weekly top-rated Martingale picks — our highest-conviction plays" },
  { icon: Layers, text: "Arbitrage scanner — auto-detect locked-in edge opportunities across US books" },
  { icon: Mail, text: "Daily morning email digest with your bankroll-sized progression plan" },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const startTrial = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "weekly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not start checkout"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain relative text-foreground">
      <Seo
        title="Pricing · 3 Days Free, Then $9.99/week"
        path="/pricing"
        description="Simple flat pricing: 3 days free, then $9.99 per week for unlimited AI sports betting research, best Martingale picks across MLB/NBA/NHL/NFL/UFC, live arbitrage scanner, and daily morning email digest. Cancel anytime."
        keywords="martingale betting subscription, arbitrage scanner pricing, sports betting AI subscription, $9.99 weekly betting service"
      />
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-brand">
            <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
              <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
            </div>
            <div>
              <div className="font-display text-lg leading-none">Martingale</div>
              <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
            </div>
          </Link>
          <div className="flex gap-2">
            {user ? (
              <Link to="/app" className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]" data-testid="pricing-login">
                  Login
                </Link>
                <Link to="/register" className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#00D4FF] text-black" data-testid="pricing-register">
                  Get Access
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10 text-center">
        <div className="overline mb-6">PRICING · ONE·PLAN</div>
        <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tighter">
          Three days on the house.
          <br />
          Then <span className="text-[#00D4FF]">$9.99 a week</span>.
        </h1>
        <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
          No decoder rings, no tiers. Every Pro feature, every sport, every morning.
          Cancel whenever.
        </p>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid md:grid-cols-2 gap-0 border border-[#00D4FF]/40">
          {/* Left column — price */}
          <div className="p-10 border-b md:border-b-0 md:border-r border-[#00D4FF]/40 bg-[#00D4FF]/5">
            <div className="overline neon-text">MARTINGALE · PRO</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-7xl neon-text">$9.99</span>
              <span className="mono text-zinc-400">/week</span>
            </div>
            <div className="mt-2 text-sm text-zinc-400">
              First 3 days free. Billed weekly after. Cancel anytime.
            </div>

            <button
              onClick={startTrial}
              disabled={loading}
              data-testid="pricing-start-trial"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs hover:brightness-110 disabled:opacity-50"
            >
              {loading ? "Redirecting…" : "Start 3 Free Days"}
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>

            <div className="mt-6 mono text-[11px] text-zinc-500 leading-relaxed">
              Your 3-day free trial starts the moment you create an account. Billing at
              $9.99/week does not begin until day 4 — and it only begins if you add a card
              and choose to continue.
            </div>
          </div>

          {/* Right column — features */}
          <div className="p-10">
            <div className="overline mb-5">WHAT YOU GET</div>
            <ul className="space-y-4">
              {PRO_FEATURES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <Icon className="text-[#00D4FF] mt-0.5 shrink-0" size={18} strokeWidth={1.5} />
                  <span className="text-sm text-zinc-300 leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-zinc-800 text-xs text-zinc-500 flex items-center gap-2">
              <Check size={12} strokeWidth={1.5} /> No contracts · Cancel in one click ·
              Secured by Stripe
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
        <div className="overline mb-3">FAQ</div>
        <div className="grid md:grid-cols-3 gap-6 text-left mt-8">
          {[
            { q: "Will I be charged right away?", a: "No. Your card is only charged after the 3-day free trial ends, and only if you add one." },
            { q: "What's arbitrage betting?", a: "When one book's price and another book's price sum to less than 100% implied probability, you can bet both sides for a guaranteed profit. Our scanner finds them live." },
            { q: "How do I cancel?", a: "One click from Billing inside the app. You keep access through the end of your paid week." },
          ].map((f) => (
            <div key={f.q} className="border border-zinc-800 p-5">
              <div className="font-display text-lg text-white mb-2">{f.q}</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-zinc-800 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-3 text-xs mono text-zinc-500">
          <div>© 2026 MARTINGALE PLAYBOOK</div>
          <div className="amber-text">GAMBLE RESPONSIBLY · 1-800-GAMBLER</div>
        </div>
      </footer>
    </div>
  );
}